import React, { useEffect, useState, useCallback } from "react";
import { api, apiError, mediaUrl } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import * as Icons from "lucide-react";
import { Plus, Pencil, Trash2, Loader2, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const ICON_OPTIONS = ["ShoppingBasket", "Shirt", "Smartphone", "Sofa", "Sparkles", "Palette", "Package", "Gift", "Watch", "BookOpen", "Coffee", "Car", "Heart", "Leaf", "Wrench", "Baby"];

const IconEl = ({ name, className }) => {
  const C = Icons[name] || Icons.ShoppingBasket;
  return <C className={className} />;
};

export const CategoryManager = () => {
  const { t, lang } = useLang();
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ fr: "", en: "", icon: "ShoppingBasket", image: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => api.get("/categories").then((r) => setCats(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ fr: "", en: "", icon: "ShoppingBasket", image: "" }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ fr: c.fr, en: c.en, icon: c.icon || "ShoppingBasket", image: c.image || "" }); setOpen(true); };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: data.url }));
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); e.target.value = ""; }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/categories/${editing.id}`, form);
      else await api.post("/categories", form);
      toast.success(t("save"));
      setOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };

  const del = async (c) => {
    try { await api.delete(`/categories/${c.id}`); toast.success(t("delete")); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        <Button onClick={openNew} className="rounded-full bg-mango-600 hover:bg-mango-700" data-testid="add-category-btn"><Plus className="w-4 h-4 mr-1" />{t("add_category")}</Button>
      </div>
      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Image</TableHead><TableHead>{t("icon")}</TableHead><TableHead>{t("category_fr")}</TableHead><TableHead>{t("category_en")}</TableHead><TableHead>Clé</TableHead><TableHead className="text-right">{t("edit")}</TableHead></TableRow></TableHeader>
          <TableBody>
            {cats.map((c) => (
              <TableRow key={c.id} data-testid={`admin-category-${c.key}`}>
                <TableCell>
                  {c.image
                    ? <img src={mediaUrl(c.image)} alt={c.fr} className="w-14 h-12 rounded-lg object-cover bg-stone-100" data-testid={`category-img-${c.key}`} />
                    : <span className="w-14 h-12 rounded-lg bg-stone-100 grid place-items-center text-stone-300"><ImageIcon className="w-5 h-5" /></span>}
                </TableCell>
                <TableCell><span className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-800 grid place-items-center"><IconEl name={c.icon} className="w-4 h-4" /></span></TableCell>
                <TableCell className="font-medium">{c.fr}</TableCell>
                <TableCell className="text-stone-500">{c.en}</TableCell>
                <TableCell className="text-xs text-stone-400">{c.key}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-category-${c.key}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => del(c)} data-testid={`delete-category-${c.key}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="category-form">
          <DialogHeader><DialogTitle className="font-display">{editing ? t("edit") : t("add_category")}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label className="text-xs">{t("category_fr")}</Label><Input value={form.fr} onChange={(e) => setForm((f) => ({ ...f, fr: e.target.value }))} required data-testid="cat-fr" /></div>
            <div><Label className="text-xs">{t("category_en")}</Label><Input value={form.en} onChange={(e) => setForm((f) => ({ ...f, en: e.target.value }))} required data-testid="cat-en" /></div>
            <div>
              <Label className="text-xs mb-1 block">{t("icon")}</Label>
              <div className="grid grid-cols-8 gap-2 mb-2">
                {ICON_OPTIONS.map((ic) => (
                  <button type="button" key={ic} onClick={() => setForm((f) => ({ ...f, icon: ic }))} data-testid={`cat-icon-${ic}`}
                    className={`aspect-square rounded-lg grid place-items-center border transition-colors ${form.icon === ic ? "border-emerald-800 bg-emerald-50 text-emerald-800" : "border-stone-200 text-stone-500 hover:bg-stone-50"}`}>
                    <IconEl name={ic} className="w-4 h-4" />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-xs mb-1 block">Image de la catégorie</Label>
              <div className="flex items-center gap-3">
                <div className="w-24 h-20 rounded-lg overflow-hidden bg-stone-100 border border-stone-200 grid place-items-center shrink-0">
                  {form.image
                    ? <img src={mediaUrl(form.image)} alt="" className="w-full h-full object-cover" data-testid="cat-image-preview" />
                    : <ImageIcon className="w-6 h-6 text-stone-300" />}
                </div>
                <label className="cursor-pointer inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-full border border-stone-200 hover:bg-stone-50 transition-colors" data-testid="cat-image-upload-label">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {form.image ? "Changer l'image" : "Choisir une image"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="cat-image-input" />
                </label>
              </div>
              <p className="text-[11px] text-stone-400 mt-1.5">Choisissez une image depuis votre disque (affichée sur la page d'accueil).</p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">{t("cancel")}</Button>
              <Button type="submit" disabled={saving} className="flex-1 bg-emerald-800 hover:bg-emerald-900" data-testid="cat-submit">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{t("save")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
